import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, Calendar, Clock, Edit2, Flag, Newspaper, Plus, Trash2, User } from 'lucide-react';
import { apiRequest } from '../utils/api';
import { useAuth } from '../components/AuthContext';
import { NewsItem, UserRole } from '../types';
import { Button } from '../components/UI';
import { PageHero } from '../components/PageHero';
import { sanitizeHtml } from '../utils/sanitizeHtml';

type FeedItem = NewsItem & { is_scheduled?: boolean; published_at?: string };

const categoryMeta = (category: NewsItem['category']) => ({
    OFFICIAL: { label: 'Offiziell', color: '#3b82f6' },
    DECISION: { label: 'Entscheidung', color: '#22c55e' },
    ANNOUNCEMENT: { label: 'Ankündigung', color: '#f59e0b' },
}[category] || { label: category, color: '#64748b' });

const excerpt = (html: string) => html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

export const NewsFeed: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [news, setNews] = useState<FeedItem[]>([]);
    const [expandedId, setExpandedId] = useState<number | null>(null);
    const isAdmin = user?.roles.includes(UserRole.ADMIN) || user?.roles.includes(UserRole.WEBADMIN);

    const loadNews = () => {
        const userParam = user ? `?userId=${user.id}` : '';
        apiRequest<FeedItem[]>(`news.php${userParam}`).then(items => setNews(Array.isArray(items) ? items : []));
    };

    useEffect(loadNews, [user]);

    const remove = async (id: number) => {
        if (!confirm('Diesen Eintrag wirklich löschen?')) return;
        try {
            await apiRequest(`news.php?id=${id}`, 'DELETE');
            loadNews();
            if (expandedId === id) setExpandedId(null);
        } catch (error: any) { alert(error.message); }
    };

    const renderArticle = (item: FeedItem, featured = false) => {
        const meta = categoryMeta(item.category);
        const expanded = expandedId === item.id;
        const date = new Date(item.published_at || item.created_at);
        return (
            <article key={item.id} className={`group relative isolate overflow-hidden rounded-[2rem] border border-white/[0.075] bg-[#111218] shadow-[0_25px_70px_rgba(0,0,0,.22)] transition duration-300 hover:border-white/[0.15] ${featured ? 'min-h-[340px] md:min-h-[410px]' : 'min-h-[280px]'}`}>
                <div className="absolute inset-x-0 top-0 h-[3px]" style={{ background: `linear-gradient(90deg, ${meta.color}, transparent 78%)` }} />
                <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full opacity-[0.1] blur-[75px]" style={{ backgroundColor: meta.color }} />
                <Newspaper className="pointer-events-none absolute -bottom-10 -right-8 h-48 w-48 opacity-[0.022]" />
                <div className={`relative flex h-full flex-col ${featured ? 'p-7 md:p-10' : 'p-6'}`}>
                    <div className="flex items-start justify-between gap-4">
                        <span className="rounded-full border px-3 py-1.5 text-[8px] font-black uppercase tracking-[.18em]" style={{ color: meta.color, borderColor: `${meta.color}35`, backgroundColor: `${meta.color}10` }}>{meta.label}</span>
                        {item.is_scheduled && <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/15 bg-amber-300/10 px-3 py-1.5 text-[8px] font-black uppercase tracking-wider text-amber-200"><Clock size={10} /> Geplant</span>}
                    </div>

                    <div className="flex-1">
                        <h2 className={`mt-7 max-w-4xl font-black uppercase italic leading-[.98] tracking-[-.035em] text-white ${featured ? 'text-3xl md:text-5xl' : 'text-2xl'}`}>{item.title}</h2>
                        {!expanded && <p className={`mt-4 max-w-3xl text-sm leading-6 text-white/38 ${featured ? 'line-clamp-4' : 'line-clamp-3'}`}>{excerpt(item.content)}</p>}
                        {expanded && <div className="custom-html-content mt-6 border-t border-white/[0.06] pt-6 text-sm leading-7 text-white/65" dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.content) }} />}
                    </div>

                    <div className="mt-7 flex flex-col gap-4 border-t border-white/[0.06] pt-5 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[9px] font-bold uppercase tracking-wider text-white/25">
                            <span className="inline-flex items-center gap-1.5"><Calendar size={11} />{date.toLocaleDateString('de-DE')}</span>
                            <span className="inline-flex items-center gap-1.5"><User size={11} />{item.author_name || 'SDC Redaktion'}</span>
                            {item.track_location && <span className="inline-flex items-center gap-1.5 text-blue-200/55"><Flag size={11} />{item.track_location}</span>}
                        </div>
                        <div className="flex items-center gap-2">
                            {isAdmin && <><button onClick={() => navigate(`/admin/news/edit/${item.id}`)} aria-label="Beitrag bearbeiten" className="rounded-xl border border-white/[0.07] bg-white/[0.04] p-2.5 text-white/35 transition hover:text-white"><Edit2 size={14} /></button><button onClick={() => remove(item.id)} aria-label="Beitrag löschen" className="rounded-xl border border-red-400/10 bg-red-400/[0.06] p-2.5 text-red-300/45 transition hover:text-red-300"><Trash2 size={14} /></button></>}
                            <button onClick={() => setExpandedId(expanded ? null : item.id)} className="inline-flex items-center gap-2 rounded-xl border border-white/[0.08] bg-white/[0.055] px-4 py-2.5 text-[9px] font-black uppercase tracking-wider text-white transition hover:border-white/20">{expanded ? 'Schließen' : 'Weiterlesen'}<ArrowUpRight size={13} /></button>
                        </div>
                    </div>
                </div>
            </article>
        );
    };

    return (
        <div className="sdc-feature-page sdc-news-page mx-auto max-w-7xl space-y-6 pb-24 animate-fade-in">
            <PageHero eyebrow="Paddock Journal" title="Neuigkeiten" description="Die redaktionelle Zentrale der Liga – Rennberichte, Entscheidungen und offizielle Meldungen in einem modernen Journal." icon={Newspaper} accent="#3b82f6" stats={[{ label: 'Beiträge', value: news.length }, { label: 'Offiziell', value: news.filter(item => item.category === 'OFFICIAL').length }, { label: 'Entscheidungen', value: news.filter(item => item.category === 'DECISION').length }]} actions={isAdmin ? <Button icon={Plus} onClick={() => navigate('/admin/news/create')}>Beitrag erstellen</Button> : undefined} />

            {news.length === 0 ? <div className="sdc-feature-panel p-14 text-center"><Newspaper className="mx-auto text-white/15" size={42} /><p className="mt-4 text-sm text-white/30">Noch keine Meldungen veröffentlicht.</p></div> : <>
                {renderArticle(news[0], true)}
                {news.length > 1 && <div className="grid gap-4 lg:grid-cols-2">{news.slice(1).map(item => renderArticle(item))}</div>}
            </>}
        </div>
    );
};
